import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useScrollLock } from '@/hooks/useScrollLock';
import './menu.css';

/**
 * A hand-written WAI-ARIA APG menu (ADR 0019).
 *
 * No new dependency: this is the whole of the pattern — `role="menu"` in a
 * portal, roving DOM focus between `menuitem`s, `Up`/`Down`/`Home`/`End`,
 * `Right`/`Left` for a submenu, type-ahead, `Enter`/`Space` to activate and
 * `Escape` to close.
 *
 * ## Why focus restore is a prop, not `document.activeElement`
 *
 * The caller is a virtualized `grid` whose *container* is the only element that
 * ever holds focus (see `MessageList.tsx`): the row a menu was opened on can be
 * unmounted by virtualization while the menu is open, so restoring focus to
 * "whatever had it" — or to the row — would drop focus to `<body>` and kill
 * every subsequent keystroke. `returnFocusTo` is therefore explicit, and every
 * close path (Escape, activation, outside pointerdown, Tab) runs through
 * `close()`, which focuses it exactly once with `preventScroll`.
 *
 * ## The sheet variant (ADR 0048 §6)
 *
 * On handheld the same menu renders as a bottom sheet: a scrim, the panel
 * pinned to the bottom edge with 48-px rows, a submenu as a second sheet over
 * the first, and the document scroll-locked underneath. Only the container
 * changes. The roles, the roving focus, every key and every close path are the
 * ones above, so a keyboard on a tablet drives it exactly as it drives the
 * popover.
 */

export interface MenuPoint {
  x: number;
  y: number;
}

/** Anchor at a pointer position, or under an element's box. */
export type MenuAnchor = MenuPoint | DOMRect;

/** A floating menu at the anchor, or a bottom sheet (handheld). */
export type MenuVariant = 'popover' | 'sheet';

export interface MenuActionNode {
  kind: 'item';
  id: string;
  label: string;
  /** Rendered right-aligned; the menu is also how the keymap is discovered. */
  shortcut?: string;
  /** Indentation level — the flattened Move-to list keeps the folder tree legible. */
  depth?: number;
  /**
   * Offered, but not available here, with the reason under the label.
   *
   * Shown rather than hidden when the item's absence would be the puzzle:
   * *Label as…* on a selection spanning two mailboxes (a label belongs to one
   * account), or another mailbox's folder while offline (ADR 0108) — silently
   * dropping the item from a menu the user knows is there reads as a bug.
   *
   * `aria-disabled`, not `disabled`: a disabled button is unreachable by
   * keyboard, and the reason is the point of showing it (APG's rule for menu
   * items). Activating it does nothing and does not close the menu.
   */
  disabled?: boolean;
  /** The sentence under the label. Only meaningful with `disabled`. */
  hint?: string;
  /**
   * The heading this item sits under: the mailbox a folder belongs to in the
   * multi-mailbox Move to / Copy to lists (ADR 0108). Consecutive items with
   * the same group are one `role="group"` named by it.
   */
  group?: string;
  /**
   * Painted in the danger colour: the item destroys something (Delete…, an
   * Empty that deletes for good). A tint, not a guard — the dialog the item
   * opens is what asks.
   */
  danger?: boolean;
  onSelect: () => void;
}

export interface MenuSubmenuNode {
  kind: 'submenu';
  id: string;
  label: string;
  shortcut?: string;
  items: MenuActionNode[];
  /** Accessible name of the filter row shown when the submenu is long. */
  filterLabel?: string;
}

export interface MenuSeparatorNode {
  kind: 'separator';
  id: string;
}

export type MenuNode = MenuActionNode | MenuSubmenuNode | MenuSeparatorNode;

/** A node that can hold focus (everything but a separator). */
type FocusableNode = MenuActionNode | MenuSubmenuNode;

export interface MenuProps {
  /** Accessible name of the root menu. */
  label: string;
  /** Optional non-interactive first line (e.g. "3 messages"). */
  header?: string;
  items: MenuNode[];
  anchor: MenuAnchor;
  /**
   * Where focus goes on close. For the message list this is the grid
   * container — never a row, which may have been virtualized away.
   */
  returnFocusTo: HTMLElement | null;
  onClose: () => void;
  /** Open this submenu immediately (the detached `V` Move-to palette). */
  openSubmenuId?: string;
  /** `sheet` ignores `anchor` and rises from the bottom edge. */
  variant?: MenuVariant;
}

/**
 * Runs of nodes that share a `group`, in order: the Move-to list of several
 * mailboxes renders each run as one named `role="group"`.
 */
function segmentsOf(nodes: readonly MenuNode[]): { key: number; group: string | undefined; nodes: MenuNode[] }[] {
  const out: { key: number; group: string | undefined; nodes: MenuNode[] }[] = [];
  for (const node of nodes) {
    const group = node.kind === 'item' ? node.group : undefined;
    const last = out[out.length - 1];
    if (last !== undefined && last.group === group) last.nodes.push(node);
    else out.push({ key: out.length, group, nodes: [node] });
  }
  return out;
}

/** Above this many entries a submenu grows a type-ahead filter row (ADR 0019). */
export const SUBMENU_FILTER_THRESHOLD = 12;

const TYPEAHEAD_RESET_MS = 600;
const VIEWPORT_MARGIN = 8;

/**
 * Where the panel is placed: the pointer position, or the bottom-left corner of
 * the anchoring element's box. A DOMRect carries `x`/`y` too, so the
 * discriminator is a box-only property.
 */
function anchorPoint(anchor: MenuAnchor): MenuPoint {
  if ('bottom' in anchor) return { x: anchor.left, y: anchor.bottom };
  return { x: anchor.x, y: anchor.y };
}

/**
 * Where a root panel goes when it will not fit at `anchorPoint`: the way a
 * native context menu behaves. Off the right edge it hangs leftwards from the
 * pointer (or right-aligns with the anchoring box); off the bottom it opens
 * UPWARDS from the pointer (or sits above the box) instead of being clamped
 * up over the very row it was opened on.
 */
function anchorFlip(anchor: MenuAnchor): { x: number; y: number } {
  if ('bottom' in anchor) return { x: anchor.right, y: anchor.top };
  return { x: anchor.x, y: anchor.y };
}

export function Menu({
  label,
  header,
  items,
  anchor,
  returnFocusTo,
  onClose,
  openSubmenuId,
  variant = 'popover',
}: MenuProps) {
  const layerRef = useRef<HTMLDivElement>(null);
  const closed = useRef(false);
  const sheet = variant === 'sheet';
  // A sheet is a layer over a scrolling document: the page stays still under
  // it, as under every other handheld sheet.
  useScrollLock(sheet);

  const close = useCallback(() => {
    if (closed.current) return;
    closed.current = true;
    returnFocusTo?.focus({ preventScroll: true });
    onClose();
  }, [onClose, returnFocusTo]);

  const activate = useCallback(
    (item: MenuActionNode) => {
      // Close first, so focus is back on the grid before the action runs and a
      // dialog the action opens can take it from there.
      close();
      item.onSelect();
    },
    [close],
  );

  // Outside press closes. Capture phase, so it also beats handlers on the page.
  useEffect(() => {
    const onDown = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && layerRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [close]);

  // A scroll moves the row the menu was opened on out from under it, so the
  // menu goes with it. Capture phase, because the scroller is the grid's own
  // container and not the window; a scroll *inside* the menu (a long folder
  // list) is the one scroll that must not close it. Nor does a scroll of
  // something the opener is not in: the reading pane's Move to… hangs under
  // the pane's toolbar, and a conversation settling on its newest message as
  // its bodies arrive scrolled beside it and took the menu away as it opened.
  // A sheet is not anchored to a row, and the document under it is locked.
  useEffect(() => {
    if (sheet) return;
    const onScroll = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && layerRef.current?.contains(target)) return;
      if (returnFocusTo && target instanceof Element && !target.contains(returnFocusTo)) return;
      close();
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [close, returnFocusTo, sheet]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="menu-layer" data-variant={variant} ref={layerRef}>
      {/* The scrim is inside the layer, so the outside-press rule above never
          sees it; tapping it is its own close path. A real button, so it is
          one for a screen reader too, and out of the tab order like a scrim. */}
      {sheet && (
        <button
          type="button"
          className="menu-scrim"
          aria-label="Close menu"
          tabIndex={-1}
          onClick={close}
        />
      )}
      <MenuPanel
        sheet={sheet}
        label={label}
        header={header}
        items={items}
        point={anchorPoint(anchor)}
        flipX={anchorFlip(anchor).x}
        flipY={anchorFlip(anchor).y}
        onActivate={activate}
        onCloseAll={close}
        {...(openSubmenuId !== undefined ? { initialSubmenuId: openSubmenuId } : {})}
      />
    </div>,
    document.body,
  );
}

interface MenuPanelProps {
  label: string;
  header?: string | undefined;
  items: MenuNode[];
  point: MenuPoint;
  /**
   * The x a panel flips to when it will not fit to the right of `point`: the
   * left edge of the item (or the pointer) it came from. Root menus have none.
   */
  flipX?: number;
  /**
   * The y a panel flips UP to (its bottom edge) when it will not fit below
   * `point`. Only a root menu has one; a submenu is clamped.
   */
  flipY?: number;
  onActivate: (item: MenuActionNode) => void;
  onCloseAll: () => void;
  /** Present on a submenu: `Left`/`Escape` close this level only. */
  onCloseSelf?: () => void;
  initialSubmenuId?: string;
  /** A submenu shows a filter row above this many entries. */
  filterLabel?: string;
  /** Rendered as a bottom sheet: no anchoring, no viewport clamp. */
  sheet?: boolean;
}

function MenuPanel({
  label,
  header,
  items,
  point,
  flipX,
  flipY,
  onActivate,
  onCloseAll,
  onCloseSelf,
  initialSubmenuId,
  filterLabel,
  sheet = false,
}: MenuPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const typeahead = useRef<{ buffer: string; at: number }>({ buffer: '', at: 0 });

  const filterable = filterLabel !== undefined && items.length > SUBMENU_FILTER_THRESHOLD;
  const [filter, setFilter] = useState('');

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle === '') return items;
    return items.filter(
      (node) => node.kind !== 'separator' && node.label.toLowerCase().includes(needle),
    );
  }, [items, filter]);

  const focusables = useMemo(
    () => visible.filter((node): node is FocusableNode => node.kind !== 'separator'),
    [visible],
  );

  // Initial focus is lazy state rather than an effect: a filter row owns focus
  // when it exists, otherwise the first item does. Not in a sheet: focusing a
  // text field on a phone raises the keyboard over the list it filters, so
  // there the first item has focus and the filter is one tap away.
  const [activeId, setActiveId] = useState<string | undefined>(() =>
    filterable && !sheet ? undefined : focusables[0]?.id,
  );
  /**
   * `initialSubmenuId` is the detached Move-to palette (`V`). It is seeded as
   * initial state, not opened from an effect: at first paint there is no item
   * rect to anchor to, and the submenu clamps itself into the viewport anyway.
   */
  const [openSubmenu, setOpenSubmenu] = useState<
    { id: string; point: MenuPoint; flipX: number } | undefined
  >(() =>
    initialSubmenuId === undefined
      ? undefined
      : { id: initialSubmenuId, point: { x: point.x, y: point.y }, flipX: point.x },
  );

  const activeNode = focusables.find((node) => node.id === activeId);
  const submenuNode =
    openSubmenu === undefined
      ? undefined
      : focusables.find(
          (node): node is MenuSubmenuNode => node.kind === 'submenu' && node.id === openSubmenu.id,
        );

  const openSubmenuFor = useCallback((node: MenuSubmenuNode) => {
    const rect = itemRefs.current.get(node.id)?.getBoundingClientRect();
    setActiveId(node.id);
    setOpenSubmenu({
      id: node.id,
      point: rect ? { x: rect.right, y: rect.top } : { x: 0, y: 0 },
      flipX: rect ? rect.left : 0,
    });
  }, []);

  // Roving focus. While a submenu is open focus belongs to it, so this level
  // deliberately does not take it back.
  useEffect(() => {
    if (openSubmenu !== undefined) return;
    if (filterable && activeId === undefined) {
      filterRef.current?.focus();
      return;
    }
    if (activeId === undefined) {
      panelRef.current?.focus();
      return;
    }
    itemRefs.current.get(activeId)?.focus();
  }, [activeId, filterable, openSubmenu]);

  /**
   * Keep the panel inside the viewport once it has been measured.
   *
   * Written straight to `style` rather than through state: the anchor is the
   * truth, the clamp is a correction to it, and a render pass per measurement
   * would only make the menu flash.
   *
   * A submenu that will not fit to the right is *flipped* to hang off the left
   * edge of its parent item rather than merely clamped: clamping would slide it
   * back over the parent menu, hiding the item it belongs to and every item
   * around it — which reads as "the submenu did not open".
   */
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || sheet) return;
    const { width, height } = panel.getBoundingClientRect();
    const overflowsRight = point.x + width > window.innerWidth - VIEWPORT_MARGIN;
    const x = overflowsRight && flipX !== undefined ? flipX - width : point.x;
    const overflowsBottom = point.y + height > window.innerHeight - VIEWPORT_MARGIN;
    const y = overflowsBottom && flipY !== undefined ? flipY - height : point.y;
    const maxX = window.innerWidth - width - VIEWPORT_MARGIN;
    const maxY = window.innerHeight - height - VIEWPORT_MARGIN;
    panel.style.left = `${Math.max(VIEWPORT_MARGIN, Math.min(x, Number.isFinite(maxX) ? maxX : x))}px`;
    panel.style.top = `${Math.max(VIEWPORT_MARGIN, Math.min(y, Number.isFinite(maxY) ? maxY : y))}px`;
  });

  const step = useCallback(
    (delta: number) => {
      if (focusables.length === 0) return;
      const current = focusables.findIndex((node) => node.id === activeId);
      const from = current < 0 ? (delta > 0 ? -1 : 0) : current;
      const next = (from + delta + focusables.length) % focusables.length;
      setActiveId(focusables[next]?.id);
    },
    [activeId, focusables],
  );

  const activateNode = useCallback(
    (node: FocusableNode | undefined) => {
      if (!node) return;
      // An `aria-disabled` item is focusable and readable but does nothing,
      // and does not close the menu: the reason under its label is why it is
      // on screen at all.
      if (node.kind === 'item' && node.disabled) return;
      if (node.kind === 'submenu') openSubmenuFor(node);
      else onActivate(node);
    },
    [onActivate, openSubmenuFor],
  );

  const closeSubmenu = useCallback(() => {
    const id = openSubmenu?.id;
    setOpenSubmenu(undefined);
    if (id !== undefined) setActiveId(id);
  }, [openSubmenu]);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const inFilter = event.target === filterRef.current;

      switch (event.key) {
        case 'ArrowDown':
          step(1);
          break;
        case 'ArrowUp':
          step(-1);
          break;
        case 'Home':
          if (inFilter) return;
          setActiveId(focusables[0]?.id);
          break;
        case 'End':
          if (inFilter) return;
          setActiveId(focusables[focusables.length - 1]?.id);
          break;
        case 'ArrowRight':
          // In the filter row the horizontal arrows are caret movement; taking
          // them would make the text unusable to edit.
          if (inFilter || activeNode?.kind !== 'submenu') return;
          openSubmenuFor(activeNode);
          break;
        case 'ArrowLeft':
          if (inFilter || !onCloseSelf) return;
          onCloseSelf();
          break;
        case 'Escape':
          // A submenu closes itself; the root closes the whole menu and hands
          // focus back to the grid container.
          if (onCloseSelf) onCloseSelf();
          else onCloseAll();
          break;
        case 'Tab':
          onCloseAll();
          break;
        case 'Enter':
        case ' ': {
          if (inFilter && event.key === ' ') return;
          const target = activeNode ?? (inFilter ? focusables[0] : undefined);
          activateNode(target);
          break;
        }
        default: {
          // Type-ahead: skipped inside the filter row, where letters are text.
          if (inFilter || event.key.length !== 1) return;
          const now = Date.now();
          const state = typeahead.current;
          state.buffer = now - state.at > TYPEAHEAD_RESET_MS ? event.key : state.buffer + event.key;
          state.at = now;
          const needle = state.buffer.toLowerCase();
          const match =
            focusables.find(
              (node, index) =>
                index > focusables.findIndex((n) => n.id === activeId) &&
                node.label.toLowerCase().startsWith(needle),
            ) ?? focusables.find((node) => node.label.toLowerCase().startsWith(needle));
          if (!match) return;
          setActiveId(match.id);
          break;
        }
      }

      event.preventDefault();
      event.stopPropagation();
    },
    [activateNode, activeId, activeNode, focusables, onCloseAll, onCloseSelf, openSubmenuFor, step],
  );

  const groupIdBase = useId();
  const renderNode = (node: MenuNode) =>
    node.kind === 'separator' ? (
      <div key={node.id} className="menu-separator" role="separator" />
    ) : (
      <button
        key={node.id}
        type="button"
        role="menuitem"
        className="menu-item"
        // Roving tabindex: the active item (or the first, before any is)
        // is the menu's one tab stop. Tab itself still closes the menu
        // (onKeyDown); this is what makes the scrolling panel reachable
        // at all for a keyboard (axe scrollable-region-focusable).
        tabIndex={node.id === (activeId ?? focusables[0]?.id) ? 0 : -1}
        data-active={node.id === activeId}
        {...(node.kind === 'submenu'
          ? { 'aria-haspopup': 'menu' as const, 'aria-expanded': openSubmenu?.id === node.id }
          : {})}
        style={
          node.kind === 'item' && node.depth
            ? { paddingInlineStart: `calc(var(--space-3) + ${node.depth} * var(--space-4))` }
            : undefined
        }
        ref={(element) => {
          if (element) itemRefs.current.set(node.id, element);
          else itemRefs.current.delete(node.id);
        }}
        onMouseEnter={() => {
          // Hover is how a submenu is found with a mouse: pointing at the
          // parent item opens it, and moving onto any other item closes it
          // again, so the mouse is never left with two levels showing.
          if (node.kind === 'submenu') {
            if (openSubmenu?.id !== node.id) openSubmenuFor(node);
            return;
          }
          if (openSubmenu !== undefined) setOpenSubmenu(undefined);
          setActiveId(node.id);
        }}
        {...(node.kind === 'item' && node.disabled ? { 'aria-disabled': true } : {})}
        {...(node.kind === 'item' && node.danger ? { 'data-tone': 'danger' } : {})}
        onClick={() => activateNode(node)}
      >
        <span className="menu-item__label">
          {node.label}
          {node.kind === 'item' && node.hint !== undefined && (
            <span className="menu-item__hint">{node.hint}</span>
          )}
        </span>
        {node.shortcut !== undefined && (
          <span className="menu-item__shortcut">{node.shortcut}</span>
        )}
        {node.kind === 'submenu' && <span className="menu-item__arrow" aria-hidden="true">▸</span>}
      </button>
    );

  return (
    <>
      <div
        ref={panelRef}
        className="menu-panel"
        role="menu"
        aria-label={label}
        aria-orientation="vertical"
        tabIndex={-1}
        // A sheet's submenu is a second sheet over this one; this one steps
        // out of sight rather than showing above it.
        {...(sheet && openSubmenu !== undefined ? { 'data-covered': 'true' } : {})}
        style={sheet ? undefined : { left: `${point.x}px`, top: `${point.y}px` }}
        onKeyDown={onKeyDown}
        // The native menu must not appear on top of ours.
        onContextMenu={(event) => event.preventDefault()}
      >
        {header !== undefined && (
          <div className="menu-header" role="presentation">
            {header}
          </div>
        )}

        {filterable && (
          <div className="menu-filter" role="presentation">
            <input
              ref={filterRef}
              type="text"
              className="menu-filter__input"
              aria-label={filterLabel}
              placeholder={filterLabel}
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value);
                setActiveId(undefined);
              }}
            />
          </div>
        )}

        {segmentsOf(visible).map((segment) =>
          segment.group === undefined ? (
            <Fragment key={`run-${segment.key}`}>{segment.nodes.map(renderNode)}</Fragment>
          ) : (
            <div
              key={`group-${segment.key}`}
              role="group"
              aria-labelledby={`${groupIdBase}-${segment.key}`}
              className="menu-group"
            >
              <div id={`${groupIdBase}-${segment.key}`} className="menu-group__label" role="presentation">
                {segment.group}
              </div>
              {segment.nodes.map(renderNode)}
            </div>
          ),
        )}

        {focusables.length === 0 && (
          <div className="menu-empty" role="presentation">
            No matches
          </div>
        )}
      </div>

      {submenuNode && openSubmenu && (
        <MenuPanel
          sheet={sheet}
          label={submenuNode.label}
          items={submenuNode.items}
          point={openSubmenu.point}
          flipX={openSubmenu.flipX}
          onActivate={onActivate}
          onCloseAll={onCloseAll}
          onCloseSelf={closeSubmenu}
          {...(submenuNode.filterLabel !== undefined ? { filterLabel: submenuNode.filterLabel } : {})}
        />
      )}
    </>
  );
}
